/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { SessionCreateDrmTypeEnum, SessionTrickModeRestriction } from 'client-code/session-manager';
import {
    StationPolicy,
    StreamingDeviceType,
    StreamingRestrictionType,
} from 'client-code/channels-service';
import {
    TransportEncodingType,
    VideoProviderConfiguration,
} from 'client-code/video-provider-configurations';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { Storage } from '../../storage/Storage';
import { Session } from '../../entities/Session';
import { Log, VuLogger, QoEAppLogger } from '../../logging';
import { ErrorTypes } from '../../error';
import { Channel } from '../../entities/Channel';
import { TrackUtils } from '../../utils/TrackUtils';
import { Constants } from '../../constants';
import { TInHomeLocation } from '../../types/TInHomeLocation';
import { DeviceModel } from '../../device';
import { ShakaPlayerImpl } from '../shaka/ShakaPlayerImpl';
import { IServices } from '../../interfaces/IServices';
import { ServicesClientImpl } from '../../impls/ServicesClientImpl';
import { PlaybackError } from '../../error/PlaybackError';
import { PlaybackErrorType, PlaybackState } from '../enums';
import { QoERegionModeType } from '../../logging/qoe/QoERegionModeType';
import {
    IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD,
    SEEK_BACKWARD_8,
    SEEK_FORWARD_30,
    SPEED,
} from '../../../config';
import {
    IVideoPlayer,
    IPlaybackIntervalInfo,
    VideoPlayerModel,
    IPlaybackError,
} from '../interfaces';
import { LiveTvLocationError, LiveTvPlaybackError } from '../error';
import { SocuDataModelImpl } from '../../socu/impls';
import { PlaybackContentType } from '../../socu/enums/PlaybackContentType';
import { IpLinearStreamingSessionImpl } from './IpLinearStreamingSessionImpl';
import { SocuStreamingSessionImpl } from './SocuStreamingSessionImpl';
import { StreamingSessionImpl } from './StreamingSessionImpl';

/* eslint-disable class-methods-use-this */
export class VideoPlayerModelImpl implements VideoPlayerModel {
    TAG: string = 'VideoPlayerModelImpl';

    private mPlayer: IVideoPlayer;

    private static oVideoPlayerModel: VideoPlayerModel;

    private static mPlaybackErrorCallback: Function = () => null;

    static mStreamRestrictionChangeCallback: Function = () => null;

    private mIStreamingSession: StreamingSessionImpl;

    private mIsBuffering: boolean;

    private mStreamRestricted: boolean;

    private subtitleTracks: any[] = [];

    private mAudioTracksMap: Map<number, string> = new Map();

    private mAudioTrackIds: number[] = [];

    private deviceType: StreamingDeviceType;

    private trickplayRestrictions: SessionTrickModeRestriction | undefined;

    private inHomeLocationData: TInHomeLocation;

    private playbackGracePeriodForLocation: any = null;

    private mStreamingUrl: string = '';

    private drmServerType: string = '';

    private mCurrentAudioSelectedTrack: string = '';

    private constructor() {
        // Browser and SmartTv native app to use shaka player
        Diagnostics.debug(this.TAG, 'using shaka player');
        Log.d(this.TAG, 'using shaka player');
        this.mPlayer = ShakaPlayerImpl.getInstance();
        QoEAppLogger.setShakaPlayerInstance(this.mPlayer);
        this.mIStreamingSession = IpLinearStreamingSessionImpl.getInstance();
        this.mIsBuffering = true;
        this.mStreamRestricted = false;
        this.deviceType = <StreamingDeviceType>DeviceModel.getTiVoSettings().getDeviceType();
        // TODO: ANIKUMAR - Eleminate the direct access of storage.
        this.inHomeLocationData = Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
    }

    restartPlaybackWithPosition(position: number) {
        this.mPlayer.play(this.mStreamingUrl, position)
    }

    static getInstance(): VideoPlayerModel {
        if (!VideoPlayerModelImpl.oVideoPlayerModel) {
            VideoPlayerModelImpl.oVideoPlayerModel = new VideoPlayerModelImpl();
        }
        return VideoPlayerModelImpl.oVideoPlayerModel;
    }

    private handleSessionPlaylistUrl(sessionPlayListUri: string): void {
        if (sessionPlayListUri.length === 0 && this.mStreamingUrl.length === 0) {
            this.handlePlaybackError(ErrorTypes.LIVE_TV_PLAYLIST_URI_MISSING_V908);
            return;
        }
        const playUri = sessionPlayListUri.replace(/^http?:\/\//, 'https://');
        if (this.mStreamingUrl.length > 0) {
            if (this.mStreamingUrl !== playUri) {
                Diagnostics.info(this.TAG, 'playUri is different from session response');
                Log.i(this.TAG, 'playUri is different from session response');
                this.mStreamingUrl = playUri;
                this.restartPlayback();
                VuLogger.playUrl = this.mStreamingUrl;
            }
        } else {
            this.mStreamingUrl = playUri;
            this.mPlayer.play(this.mStreamingUrl);
        }
    }

    private restartPlayback() {
        Diagnostics.info(
            this.TAG,
            'Restart playback as playlist uri is different in session response',
        );
        Log.i(this.TAG, 'Restart playback as playlist uri is different in session response');
        QoEAppLogger.logDifferentURI();

        QoEAppLogger.updateStreamingUrl(this.mStreamingUrl);
        QoEAppLogger.logWatchContentStart();
        this.mPlayer.cleanUp();
        this.mPlayer.play(this.mStreamingUrl);
        this.resetInternal();
    }

    // eslint-disable-next-line class-methods-use-this
    private onPlaybackErrorHandler(error: any): void {
        VideoPlayerModelImpl.mPlaybackErrorCallback(error);
    }

    // eslint-disable-next-line class-methods-use-this
    private onStreamRestrictionChangeHandler(): void {
        VideoPlayerModelImpl.mStreamRestrictionChangeCallback(this.mStreamRestricted);
    }

    private setStreamRestriction(status: boolean) {
        this.mStreamRestricted = status;
        this.onStreamRestrictionChangeHandler();
    }

    addPlaybackErrorListener(callback: Function): void {
        VideoPlayerModelImpl.mPlaybackErrorCallback = callback;
    }

    addStreamRestrictionChangeListener(callback: Function): void {
        VideoPlayerModelImpl.mStreamRestrictionChangeCallback = callback;
    }

    public getVideoPlayer(): IVideoPlayer {
        return this.mPlayer;
    }

    configure(config: any): void {
        Diagnostics.debug(this.TAG, 'configure');
        Log.d(this.TAG, 'configure');
        if (!this.mPlayer) {
            Diagnostics.error(this.TAG, 'Player object initilisation failed');
            Log.e(this.TAG, 'Player object initilisation failed');
            Diagnostics.error(this.TAG, 'Player object initilisation failed');
            this.handlePlaybackError(ErrorTypes.LIVE_TV_NOT_READY_FOR_PLAYBACK_V117);
            return;
        }
        this.mPlayer.configure(config);
    }

    playUri(channel: Channel, isOverrideEnable?: boolean, drmUri?: string): boolean {
        const { tuningUri } = channel;
        Diagnostics.debug(this.TAG, `{tuningUri: ${tuningUri}}`);
        Log.d(this.TAG, `{tuningUri: ${tuningUri}}`);
        if (tuningUri) {
            if (isOverrideEnable) {
                if (drmUri) {
                    this.mPlayer.setUpDRM('', '', '', drmUri);
                }
                this.mPlayer.play(tuningUri[0]);
            } else {
                this.fetchVPConfigurations(channel, false);
            }
            return true;
        }
        return false;
    }

    private setDrmServerType(drmServerType: string) {
        this.drmServerType = drmServerType;
    }

    public getDrmServerType(): string {
        return this.drmServerType;
    }

    private setUpDrm(response: VideoProviderConfiguration[], channel: Channel): boolean {
        let isInvalidResponse = true;
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        const partnerProviderId =
            oSocuDataModelImpl.getPlaybackContentType() === PlaybackContentType.LIVE
                ? channel.linearProviderPartnerId
                : oSocuDataModelImpl.getActionDetails()?.kernel?.partnerId || '';

        response.forEach((videoProviderConfig) => {
            const { providerPartnerId, drmConfig } = videoProviderConfig;
            if (partnerProviderId === providerPartnerId && drmConfig && isInvalidResponse) {
                const {
                    drmServerType,
                    drmServerUrl,
                    drmCustomHeader,
                    serverSuppliedDeviceIdentifier,
                    transportEncodingType,
                } = drmConfig;
                this.setDrmServerType(drmServerType);
                if (drmServerType === SessionCreateDrmTypeEnum.Widevine) {
                    if (
                        drmServerUrl &&
                        drmCustomHeader &&
                        serverSuppliedDeviceIdentifier &&
                        this.isTransportEncodingType(transportEncodingType)
                    ) {
                        this.mPlayer.setUpDRM(
                            drmServerUrl,
                            drmCustomHeader,
                            serverSuppliedDeviceIdentifier,
                        );
                        this.initPlayback(channel, drmServerType, transportEncodingType![0]);
                        isInvalidResponse = false;
                    }
                } else if (
                    drmServerType === SessionCreateDrmTypeEnum.Clear &&
                    this.isTransportEncodingType(transportEncodingType)
                ) {
                    this.resetPlayerConfiguration();
                    this.initPlayback(channel, '*', transportEncodingType![0]);
                    isInvalidResponse = false;
                }
                QoEAppLogger.updateContentPayloadDrmAndContainerInfo(
                    drmServerType,
                    transportEncodingType![0],
                );
            }
        });
        return isInvalidResponse;
    }

    private isTransportEncodingType(transportEncodingType?: TransportEncodingType[]): boolean {
        if (
            transportEncodingType &&
            transportEncodingType.length > 0 &&
            transportEncodingType[0] !== TransportEncodingType.Unknown
        )
            return true;
        return false;
    }

    private initPlayback(
        channel: Channel,
        drmServerTypeString: string,
        transportEncodingType: string,
    ) {
        const { tuningUri } = channel;
        const uri = this.getURI(tuningUri!, drmServerTypeString, transportEncodingType);
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        if (oSocuDataModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
            this.mIStreamingSession = SocuStreamingSessionImpl.getInstance();
            this.mStreamingUrl = '';
            this.mIStreamingSession.createStreamingSession({
                channel,
                transportEncodingType,
                watchAction: oSocuDataModelImpl.getActionDetails(),
            });
        } else if (uri.trim().length > 0) {
            this.mIStreamingSession = IpLinearStreamingSessionImpl.getInstance();
            this.mStreamingUrl = this.getFormattedURL(uri);
            this.mPlayer.play(this.mStreamingUrl);
            this.mIStreamingSession.createStreamingSession({
                channel,
                transportEncodingType,
                playUri: this.mStreamingUrl,
            });
            VuLogger.playUrl = this.mStreamingUrl;
        } else {
            // wait for sessionCreate response for the playback URL
            // fail only when session response dones't have playlistUrl
            this.mIStreamingSession = IpLinearStreamingSessionImpl.getInstance();
            this.mStreamingUrl = '';
            this.mIStreamingSession.createStreamingSession({
                channel,
                transportEncodingType,
                playUri: this.mStreamingUrl,
            });
            Diagnostics.debug(
                this.TAG,
                `No URI found from channel tuningUri for DRM Type: ${drmServerTypeString}`,
            );
            Log.i(
                this.TAG,
                `No URI found from channel tuningUri for DRM Type : ${drmServerTypeString}`,
            );
        }
        this.mIStreamingSession.addSessionStateChangeListener((error: IPlaybackError) => {
            this.onPlaybackErrorHandler(error);
        });
        this.mIStreamingSession.onSessionCreated.subscribe((data: Session) => {
            Diagnostics.debug(
                this.TAG,
                `onSessionCreated callback for liner/SOCU playback ${JSON.stringify(
                    data?.trickModeRestrictions,
                )}`,
            );
            Log.d(
                this.TAG,
                `onSessionCreated callback for liner/SOCU playback ${JSON.stringify(
                    data?.trickModeRestrictions,
                )}`,
            );
            this.trickplayRestrictions = data?.trickModeRestrictions;
            this.handleSessionPlaylistUrl(data?.playlistUrl!);
        });
    }

    private fetchVPConfigurations(channel: Channel, forceNetworkCall: boolean) {
        Diagnostics.debug(this.TAG, 'fetchVPConfigurations');
        Log.d(this.TAG, 'fetchVPConfigurations');
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        const providerPartnerId =
            oSocuDataModelImpl.getPlaybackContentType() === PlaybackContentType.LIVE
                ? channel.linearProviderPartnerId
                : oSocuDataModelImpl.getActionDetails()?.kernel?.partnerId || '';

        if (providerPartnerId && providerPartnerId.length) {
            const services: IServices = new ServicesClientImpl();
            services
                .fetchVPConfigurationsForVideoPlayer(providerPartnerId, forceNetworkCall)
                .then((response) => {
                    const isInvalidResponce = this.setUpDrm(response, channel);
                    if (isInvalidResponce && !forceNetworkCall) {
                        this.fetchVPConfigurations(channel, true);
                    } else if (isInvalidResponce && forceNetworkCall) {
                        if (response.length === 0) {
                            this.handlePlaybackError(
                                ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934,
                            );
                        } else {
                            this.handlePlaybackError(
                                ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909,
                            );
                        }
                        this.resetPlayerConfiguration();
                    }
                })
                .catch((error) => {
                    this.handlePlaybackError(
                        ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934,
                    );
                    Diagnostics.error(this.TAG, 'fetchVPConfigurations', error);
                    Log.e(this.TAG, 'fetchVPConfigurations', JSON.stringify(error));
                });
        } else {
            this.handlePlaybackError(
                ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909,
            );
        }
    }

    handlePlaybackError = (code: string) => {
        const playbackError: LiveTvPlaybackError = new LiveTvPlaybackError(new PlaybackError(code));

        Diagnostics.info(this.TAG, 'handlePlaybackError. messageCode: ', code);
        Log.i(this.TAG, 'handlePlaybackError. messageCode: ', code);
        QoEAppLogger.logPlaybackError(playbackError);

        this.onPlaybackErrorHandler({
            type: PlaybackErrorType.LiveTvPlaybackError,
            data: playbackError,
        });
    };

    handleLocationError = (code: string) => {
        const locationError: LiveTvLocationError = new LiveTvLocationError(new PlaybackError(code));

        Diagnostics.info(this.TAG, 'handleLocationError: ', locationError);
        Log.i(this.TAG, 'handleLocationError: ', locationError);
        QoEAppLogger.logLocationError(locationError);

        this.onPlaybackErrorHandler({
            type: PlaybackErrorType.LiveTvLocationError,
            data: locationError,
        });
    };

    private getFormattedURL = (tuneUri: string): any => {
        if (tuneUri) {
            const matches = tuneUri.match(/\bhttps?:\/\/\S+/gi);
            if (matches) {
                let url = matches![0];
                url = url.replace(/^http?:\/\//, 'https://');
                return url;
            }
        }
        return null;
    };

    private getURI(
        tuningUri: string[],
        drmServerTypeString: string,
        transportEncodingType: string,
    ): string {
        if (tuningUri !== undefined && tuningUri.length > 0) {
            let returnURI = '';
            tuningUri.forEach((uri) => {
                const splitURI = uri.split('/');
                if (
                    splitURI.includes(drmServerTypeString) &&
                    splitURI.includes(transportEncodingType)
                )
                    returnURI = uri;
            });
            return returnURI;
        }
        return '';
    }

    public trickModeRestrictions(reset?: boolean): SessionTrickModeRestriction | undefined {
        if (reset) this.trickplayRestrictions = undefined;
        return this.trickplayRestrictions;
    }

    public pause(): void {
        this.mPlayer.pause();
    }

    public unPause(): void {
        this.mPlayer.unPause();
    }

    public mute(): void {
        this.mPlayer.mute();
    }

    public unMute(): void {
        this.mPlayer.unMute();
    }

    public seekTo(position: number): void {
        this.mPlayer.seekTo(position);
    }

    public seekForward(): void {
        this.mPlayer.seekTo(SEEK_FORWARD_30);
    }

    public seekBackward(): void {
        this.mPlayer.seekTo(SEEK_BACKWARD_8);
    }

    getSeekRangeStartTimeInMs(): number {
        return this.mPlayer.getSeekRangeStartTimeInMs();
    }

    getSeekRangeEndTimeInMs(): number {
        return this.mPlayer.getSeekRangeEndTimeInMs();
    }

    public goToLive(): void {
        this.mPlayer.goToLive();
    }

    public getSeekRangeInMs(): any {
        return this.mPlayer.getSeekRangeInMs();
    }

    public getTrickPlayTimeInSeconds(): number {
        return this.mPlayer.getTrickPlayTimeInSeconds();
    }

    public setTrickPlayTimeInSeconds(time: number): void {
        return this.mPlayer.setTrickPlayTimeInSeconds(time);
    }

    public isTrickPlayEnabled(): boolean {
        return this.mPlayer.isTrickPlayEnabled();
    }

    public startFromBuffer(): void {
        this.mPlayer.startFromBuffer();
    }

    public setSpeed(rate: number): void {
        this.mPlayer.setSpeed(rate);
    }

    public getCurrentTimeInMs(): number {
        return this.mPlayer.getCurrentTimeInMs();
    }

    public getPlaybackIntervalInfo(): IPlaybackIntervalInfo {
        return {
            currentTime: this.mPlayer.getCurrentTimeInMs(),
            seekRangeStartTime: this.mPlayer.getSeekRangeStartTimeInMs(),
            seekRangeEndTime: this.mPlayer.getSeekRangeEndTimeInMs(),
            seekRangeTime: this.mPlayer.getSeekRangeInMs(),
        };
    }

    public isPlaybackStarted(): boolean {
        return this.mPlayer.isPlaybackStarted();
    }

    public selectSpeed(playbackState?: PlaybackState): void {
        switch (playbackState) {
            case PlaybackState.FF1:
                return this.setSpeed(SPEED.SPEED_FASTFORWARD_1);
            case PlaybackState.FF2:
                return this.setSpeed(SPEED.SPEED_FASTFORWARD_2);
            case PlaybackState.FF3:
                return this.setSpeed(SPEED.SPEED_FASTFORWARD_3);
            case PlaybackState.RW1:
                return this.setSpeed(SPEED.SPEED_REWIND_1);
            case PlaybackState.RW2:
                return this.setSpeed(SPEED.SPEED_REWIND_2);
            case PlaybackState.RW3:
                return this.setSpeed(SPEED.SPEED_REWIND_3);
            default:
                return this.setSpeed(SPEED.SPEED_NORMAL);
        }
    }

    public isBufferThresholdReachedStart(): boolean {
        return this.mPlayer.isBufferThresholdReachedStart();
    }

    public isBufferThresholdReachedEnd(): boolean {
        return this.mPlayer.isBufferThresholdReachedEnd();
    }

    public isBufferThresholdReached(): boolean {
        return this.mPlayer.isBufferThresholdReached();
    }

    public isTrickplayEnable(playbackState: PlaybackState) {
        return (
            playbackState === PlaybackState.FF1 ||
            playbackState === PlaybackState.FF2 ||
            playbackState === PlaybackState.FF3 ||
            playbackState === PlaybackState.RW1 ||
            playbackState === PlaybackState.RW2 ||
            playbackState === PlaybackState.RW3
        );
    }

    public resetVolumeSpeed(playbackState: PlaybackState) {
        if (this.isTrickplayEnable(playbackState)) {
            this.unMute();
            this.selectSpeed();
            QoEAppLogger.updateHealthStatsMetricsForTrickPlay(0, Date.now());
        }
    }

    public getPlayerStats = () => {
        return this.mPlayer.getPlayerStats();
    };

    private resetInternal = () => {
        this.mAudioTracksMap.clear();
        this.mAudioTrackIds = [];
    };

    stopPlayback(): void {
        this.mPlayer.stopPlayback();
        this.mPlayer.unMute();
        this.mIStreamingSession.sessionStop();
        this.resetInternal();
    }

    getSessionChannel(): Channel {
        throw new Error('Method not implemented.');
    }

    closeSession(): boolean {
        throw new Error('Method not implemented.');
    }

    isStreamingSession(): boolean {
        throw new Error('Method not implemented.');
    }

    getAudioTrackCount(): number {
        return this.mAudioTracksMap.size;
    }

    getVideoVarients() {
        return this.mPlayer.getVideoVarients();
    }

    getAudioTrackModel(): any[] {
        // this will give list of audio tracks and
        // selected track will always be on the top
        if (this.getAudioTrackCount() === 0) {
            this.mAudioTracksMap = TrackUtils.getInstance().getAudioTrackMap(
                this.mPlayer.getSupportedAudioTracks(),
            );
        }
        const currTrack = this.mPlayer.getCurrentAudioTrack();
        const currId = currTrack !== undefined ? currTrack.audioId : -1;

        const audioTracksForOverlay: any[] = [];
        this.mAudioTrackIds = [];
        if (this.mAudioTracksMap.size > 1) {
            // track a[key, value] => key is audioId and value is language string
            const mapToArray = [...Array.from(this.mAudioTracksMap)];
            this.mAudioTracksMap = new Map(mapToArray.sort((a, b) => a[1].localeCompare(b[1])));
        }
        this.mAudioTracksMap.forEach((value, key) => {
            if (key === currId) {
                this.mAudioTrackIds.unshift(key);
                audioTracksForOverlay.unshift({
                    text: value,
                    hasSelectMark: true,
                    isSelected: true,
                });
            } else {
                this.mAudioTrackIds.push(key);
                audioTracksForOverlay.push({
                    text: value,
                    hasSelectMark: true,
                    isSelected: false,
                });
            }
        });
        return audioTracksForOverlay;
    }

    getCurrentAudioTrack(): any {
        return this.mPlayer.getCurrentAudioTrack();
    }

    public getAudioVideoCodec(): string {
        const currTrack = this.mPlayer.getCurrentAudioTrack();
        if (currTrack) {
            const { videoCodec, audioCodec } = currTrack;
            return `video/${videoCodec} , audio/${audioCodec}`;
        }
        return `video/ , audio/`;
    }

    public isTextTrackSupported(): boolean {
        return this.mPlayer.isTextTrackSupported();
    }

    public isTextTrackChangeSupported(): boolean {
        return this.mPlayer.isTextTrackChangeSupported();
    }

    setCurrentAudioTrack(selectedIndex: number): void {
        if (this.getAudioTrackCount() > selectedIndex) {
            const audioId = this.mAudioTrackIds[selectedIndex];
            Diagnostics.info(this.TAG, `setCurrentAudioTrack`, audioId);
            Log.i(this.TAG, `setCurrentAudioTrack`, audioId);
            this.mPlayer.setAudioTrackByID(audioId);
            this.filterAndUpdateCurrentAudioSelectedTrack();
            VuLogger.logUserActionEvent({
                actionName: VuLogger.UserActionEventActionName.AUDIO_LANGUAGE,
                action: VuLogger.UserActionEventAction.UPDATE,
                screen: VuLogger.UserActionEventScreen.OVERLAYS,
                selection: this.getCurrentAudioSelectedTrack(),
            });
        }
    }

    getTextTrackModel(): any[] {
        // this will give list of subtitleTracks and
        // selected track always will be on the top
        const selectedLanguage = this.getSelectedCCLanguage();
        this.subtitleTracks = this.mPlayer
            .getSupportedSubtitles()
            .sort((trackItem1, trackItem2) => {
                return (
                    Number(trackItem2.language === selectedLanguage) -
                    Number(trackItem1.language === selectedLanguage)
                );
            });
        const textTracksForOverlay: any[] = [];
        this.subtitleTracks.forEach((textTrack, index) => {
            textTracksForOverlay.push({
                text: this.getDisplayableLanguage(textTrack),
                hasSelectMark: true,
                isSelected: index === 0,
            });
        });
        return textTracksForOverlay;
    }

    getClosedCaptionsStatus() {
        return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_STATUS,
            false,
        );
    }

    toggleClosedCaption(): void {
        const cc = Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_STATUS,
            false,
        );
        const selectedLanguage = this.getSelectedCCLanguage();
        if (cc) {
            this.mPlayer.displaySubtitles(!cc);
        } else {
            this.mPlayer.setSubtitlesByLanguage(selectedLanguage);
        }
    }

    public getStreamingUrl() {
        return this.mStreamingUrl;
    }

    private getSelectedCCLanguage(): string {
        return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_LANGUAGE,
            '',
        );
    }

    getCurrentTextTrack() {
        const selectedLanguage = this.getSelectedCCLanguage();
        const subtitleTrack = this.mPlayer
            .getSupportedSubtitles()
            .find(({ language }) => language === selectedLanguage);
        if (subtitleTrack) {
            return this.getDisplayableLanguage(subtitleTrack);
        }
        const activeSubtitleTrack = this.mPlayer
            .getSupportedSubtitles()
            .find(({ active }) => active === true);
        if (activeSubtitleTrack) {
            return this.getDisplayableLanguage(activeSubtitleTrack);
        }
        return '';
    }

    public filterAndUpdateCurrentAudioSelectedTrack() {
        this.mCurrentAudioSelectedTrack = VideoPlayerModelImpl.getInstance()
            .getAudioTrackModel()
            .filter((track) => track.isSelected)[0]?.text;
    }

    public getCurrentAudioSelectedTrack() {
        return this.mCurrentAudioSelectedTrack;
    }

    private getDisplayableLanguage(textTrack: any): string {
        const languageName = this.getLanguageByCode(textTrack.language);
        if (textTrack.kind === Constants.TEXT_TRACK_KIND_CAPTION) {
            return `${languageName} [${textTrack.originalTextId}]`;
        }
        return languageName;
    }

    private getLanguageByCode(code: string): string {
        return TrackUtils.getInstance().getLanguageByCode(code, Constants.ORIGINAL_LANGUAGE);
    }

    setCurrentTextTrack(selectedIndex: number): void {
        const selectedLanguage = this.subtitleTracks[selectedIndex].language;
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
            Storage.Keys.PLAYER_CC_LANGUAGE,
            selectedLanguage,
        );
        this.mPlayer.setSubtitlesByLanguage(selectedLanguage);
        VuLogger.logUserActionEvent({
            actionName: VuLogger.UserActionEventActionName.SUBTITLE_AND_CC,
            action: VuLogger.UserActionEventAction.UPDATE,
            screen: VuLogger.UserActionEventScreen.OVERLAYS,
            selection: selectedLanguage,
        });
    }

    isBuffering(): boolean {
        return this.mIsBuffering;
    }

    /**
     * TODO: Functions no longer needed; moved to PlaybackRestrictions.ts.
     * Keeping here temporarily for use in VideoplayerUI.tsx. Requires correction.
     * @deprecated
     */
    checkInBlacklist(): boolean {
        this.inHomeLocationData = Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
        if (this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            const inBlacklist = this.inHomeLocationData.response?.inBlacklist as boolean;
            if (inBlacklist) {
                QoEAppLogger.logDeviceBlacklisted();
            }
            return inBlacklist;
        }
        return false;
    }

    /**
     * TODO: Functions no longer needed; moved to PlaybackRestrictions.ts.
     * Keeping here temporarily for use in VideoplayerUI.tsx. Requires correction.
     * @deprecated
     */
    checkOutOfRegionRestriction(): boolean {
        this.inHomeLocationData = Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
        if (this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            const isInRegion = this.inHomeLocationData.response?.inRegion as boolean;
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.OUT);
            if (!isInRegion) return true;
        }

        if (this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.ERROR) {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.ERROR);
        } else if (
            this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE
        ) {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.IN_GRACE_PERIOD);
        } else {
            QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.IN);
        }
        return false;
    }

    /**
     * TODO: Functions no longer needed; moved to PlaybackRestrictions.ts.
     * Keeping here temporarily for use in VideoplayerUI.tsx. Requires correction.
     * @deprecated
     */
    checkOutOfHomeRestriction(policyInfo: Array<StationPolicy | undefined>): boolean {
        let isInHome = true;
        this.inHomeLocationData = Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
        if (this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS) {
            isInHome = this.inHomeLocationData.response?.inHome as boolean;
        }

        if (isInHome) return false;

        if (policyInfo && policyInfo.length > 0) {
            for (let i = 0; i < policyInfo.length; i += 1) {
                const streamingRestrictions =
                    policyInfo[i]?.linearStationRules?.streamingRules?.linearStreamingRestrictions;
                if (
                    streamingRestrictions?.outOfHomeStreaming?.includes(
                        StreamingRestrictionType.Blocked,
                    )
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    // TODO: ANIKUMAR - Find better place to do these operations
    checkStreamingRestrictedByLocation(policyInfo: Array<StationPolicy | undefined>) {
        // TODO: ANIKUMAR - Eleminate the direct access of storage.
        this.inHomeLocationData = Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_DATA);
        let playbackStartTime = 0;
        let playbackEndTime = 0;
        // TODO: ANIKUMAR - Eleminate the direct access of storage.
        const remainingGracePeriod =
            IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD -
            Storage.getInstance().getItem(Storage.Keys.IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD);
        if (this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.ERROR) {
            this.handleLocationError(ErrorTypes.LIVE_TV_LOCATION_RESTRICTION_OSD_ERROR_CODE_V418);
            this.setStreamRestriction(true);
        } else if (
            this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE &&
            remainingGracePeriod > 0
        ) {
            this.setStreamRestriction(false);
            playbackStartTime = Date.now();
            if (this.playbackGracePeriodForLocation === null)
                this.playbackGracePeriodForLocation = setTimeout(() => {
                    new ServicesClientImpl()
                        .fetchAndStoreInHomeLocation()
                        .then(() => {
                            this.checkStreamingRestrictedByLocation(policyInfo);
                        })
                        .catch((error) => {
                            if (error?.response?.data)
                                this.handleLocationError(
                                    ErrorTypes.LIVE_TV_LOCATION_RESTRICTION_OSD_ERROR_CODE_V418,
                                );
                            else {
                                this.handlePlaybackError(
                                    ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718,
                                );
                            }
                            Diagnostics.error(this.TAG, error);
                            Log.e(this.TAG, error);
                            this.stopPlayback();
                            this.setStreamRestriction(true);
                        })
                        .finally(() => {
                            playbackEndTime = Date.now();
                            const consumedGracePeriod = playbackEndTime - playbackStartTime;
                            // TODO: ANIKUMAR - Eleminate the direct access of storage.
                            Storage.getInstance().setItem(
                                Storage.Keys.IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD,
                                consumedGracePeriod,
                            );
                            this.playbackGracePeriodForLocation = null;
                        });
                }, remainingGracePeriod);
        } else if (
            this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.SUCCESS &&
            (this.checkInBlacklist() ||
                this.checkOutOfHomeRestriction(policyInfo) ||
                this.checkOutOfRegionRestriction())
        ) {
            this.setStreamRestriction(true);
        } else if (
            remainingGracePeriod < 0 &&
            this.inHomeLocationData.status === Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE
        ) {
            this.handlePlaybackError(ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718);
            this.setStreamRestriction(true);
        } else {
            this.setStreamRestriction(false);
        }
    }

    resetPlayerConfiguration(): void {
        this.mPlayer.resetPlayerConfiguration();
    }

    overrideAvailabilityWindow(): void {
        this.mPlayer.overrideAvailabilityWindowForStartOver();
    }
}
